import Footer from 'common/components/Footer/LandingFooter'
import Grid from 'common/components/Grid'
import Image from 'common/components/Image'
import Layout from 'common/components/Layout'
import Loader from 'common/components/Loader'
import Pagination from 'common/components/Pagination'
import themes from 'common/theme'
import cdn from 'lib/cdn'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import EmptyDataIcon from 'services/LocalStorefront/assets/svg/empty-data/empty-data.svg'
import ErrorDataIcon from 'services/LocalStorefront/assets/svg/error-data/error-data.svg'
import FilterDrawer from 'services/LocalStorefront/components/FilterDrawer'
import Header from 'services/LocalStorefront/components/Header'
import StoreCard from 'services/LocalStorefront/components/StoreCard'
import StoreDataMessage from 'services/LocalStorefront/components/StoreDataMessage'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Container = styled.div`
  width: ${themes.layout.containerWidth};
  margin-left: 50%;
  transform: translate(-50%, 0);
`

const WrapperLoader = styled.div`
  text-align: center;
`

const StyledSpan = styled.span`
  font-weight: ${({ fontWeight }) => fontWeight};
  font-size: ${({ fontSize }) => fontSize};
  color: ${themes.colors.black};
  padding: ${({ padding }) => padding};
  display: inline-block;
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 40px;
`

const BottomBanner = styled(Image)`
  display: block;
  margin-top: 40px;
`

function SearchResult(): JSX.Element {
  // HOOKS
  const router = useRouter()

  // LOCAL STATE
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false)

  // STORE STATE
  const {
    ralaliConnect: {
      isLoadingStorefront,
      storefrontData,
      storefrontMeta,
      errorStorefront,
    },
  } = useStoreState((states) => states)

  // STORE ACTION
  const {
    ralaliConnect: {
      getStorefronts,
      getProvinces,
      getCities,
      getBusinessTypes,
      getSubBusinessTypes,
    },
  } = useStoreActions((actions) => actions)

  // VARIABLE
  const queryParams = useMemo(() => {
    return {
      storeName: router.query?.q,
      provinceId: router.query?.province_id,
      cityId: router.query?.city_id,
      businessTypeId: router.query?.business_type_id,
      subBusinessTypeId: router.query?.sub_business_type_id,
    } as any
  }, [router.query])

  const isFiltered = useMemo(() => {
    return (
      !!queryParams.provinceId ||
      !!queryParams.cityId ||
      !!queryParams.businessTypeId ||
      !!queryParams.subBusinessTypeId
    )
  }, [queryParams])

  // METHOD
  const fetchStorefront = useCallback(
    async (page = 1) => {
      const { storeName, ...params } = queryParams
      await getStorefronts({
        page,
        ...params,
        ...(queryParams.storeName && { storeName }),
      })
    },
    [getStorefronts, queryParams],
  )

  const fetchProvinces = useCallback(async () => {
    await getProvinces({
      limit: 50,
    })
  }, [getProvinces])

  const fetchCities = useCallback(
    async (provinceId) => {
      await getCities({
        provinceId,
        limit: 50,
      })
    },
    [getCities],
  )

  const fetchBusinessTypes = useCallback(async () => {
    await getBusinessTypes({
      limit: 50,
    })
  }, [getBusinessTypes])

  const fetchSubBusinessTypes = useCallback(
    async (businessTypeId) => {
      await getSubBusinessTypes({
        limit: 50,
        businessTypeId,
      })
    },
    [getSubBusinessTypes],
  )

  const handleChangePage = useCallback(
    (page) => {
      fetchStorefront(page)
    },
    [fetchStorefront],
  )

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((prev) => !prev)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerVisible(false)
  }, [])

  // USE EFFECT
  useEffect(() => {
    setDrawerVisible(false)
    fetchStorefront()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, queryParams])

  useEffect(() => {
    fetchProvinces()
    fetchBusinessTypes()
    if (router.query?.province_id) {
      fetchCities(router.query.province_id)
    }
    if (router.query?.business_type_id) {
      fetchSubBusinessTypes(router.query.business_type_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout
      footer={{ component: <Footer /> }}
      header={{
        component: (
          <Header
            isFiltered={isFiltered}
            toogleFilter={toggleDrawer}
          />
        ),
      }}
    >
      <FilterDrawer isOpen={drawerVisible} onClose={closeDrawer} />
      <Container>
        {isLoadingStorefront ? (
          <WrapperLoader data-testid="wrapper-loader">
            <Loader
              primaryColor={themes.colors.primaryKonekto}
              secondaryColor={themes.colors.primaryKonekto}
            />
          </WrapperLoader>
        ) : (
          <>
            {!errorStorefront && storefrontMeta?.totalCount > 0 && (
              <>
                <StyledSpan
                  fontWeight={themes.typography.rh2.fontWeight}
                  fontSize={themes.typography.rh2.fontSize}
                  padding="40px 0"
                >
                  {`Ditemukan `}
                  <StyledSpan
                    fontWeight={themes.typography.bh2.fontWeight}
                    fontSize={themes.typography.bh2.fontSize}
                  >
                    {storefrontMeta?.totalCount}
                  </StyledSpan>
                  {queryParams.storeName
                    ? ` Store dengan kata kunci `
                    : ' Store'}
                  {queryParams.storeName && (
                    <StyledSpan
                      fontWeight={themes.typography.bh2.fontWeight}
                      fontSize={themes.typography.bh2.fontSize}
                    >
                      {`"${queryParams.storeName}"`}
                    </StyledSpan>
                  )}
                </StyledSpan>
                <Grid
                  templateColumn={3}
                  rowGap="30px"
                  columnGap="30px"
                >
                  <>
                    {storefrontData.map((store) => {
                      return (
                        <StoreCard
                          key={String(`store-${store.id}`)}
                          imageSrc={store.resizedImage}
                          name={store.storeName}
                          location={store.location}
                          productsNumber={store.totalItems}
                          href={`${store.storefrontUrl}?utm_source=ralali`}
                        />
                      )
                    })}
                  </>
                </Grid>
                {storefrontMeta?.totalCount >
                  storefrontMeta?.limit && (
                  <PaginationWrapper>
                    <Pagination
                      color={themes.colors.primaryKonekto}
                      totalData={storefrontMeta?.totalCount}
                      dropdownValues={[storefrontMeta?.limit]}
                      onChange={handleChangePage}
                      initialPage={storefrontMeta?.currentPage}
                    />
                  </PaginationWrapper>
                )}
              </>
            )}
            {!errorStorefront && storefrontMeta?.totalCount === 0 && (
              <StoreDataMessage
                marginTop="106px"
                icon={<EmptyDataIcon />}
                title="Tidak Ditemukan"
                description={
                  // eslint-disable-next-line react/jsx-wrap-multilines
                  <>
                    Kata kunci
                    <StyledSpan
                      fontWeight={themes.typography.bh2.fontWeight}
                      fontSize={themes.typography.bh2.fontSize}
                    >
                      {queryParams.storeName &&
                        ` "${queryParams.storeName}"`}
                    </StyledSpan>
                    {` tidak ditemukan. Silahkan cari kata kunci lain.`}
                  </>
                }
              />
            )}
            {errorStorefront && (
              <StoreDataMessage
                marginTop="106px"
                icon={<ErrorDataIcon />}
                title="Terjadi Kesalahan"
                description="Mohon maaf terjadi kesalahan, silahkan coba beberapa saat lagi. "
              />
            )}
          </>
        )}
      </Container>
      <a
        href="/services/ralali-connect"
        target="_blank"
        rel="noreferrer"
      >
        <BottomBanner
          objectFit="cover"
          height="420px"
          src={cdn({
            path: '/assets/img/local-store/banner-bottom.png',
          })}
        />
      </a>
    </Layout>
  )
}

export default SearchResult
